<script setup lang="ts">
import { IconTrash, IconUpload } from '@tabler/icons-vue'
import { computed, ref, watch } from 'vue'
import { useDropzone } from 'vue3-dropzone'

import BaseButton from '../BaseButton.vue'
import BaseIcon from '../BaseIcon.vue'

import type { FileWithDimensions } from './types'

export interface Props {
  thumbnail?: string
  maxFileSize?: number
  maxImageWidth?: number
  maxImageHeight?: number
  modelValue?: FileWithDimensions
  error?: string
  name: string
  id: string
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  maxImageWidth: 5000,
  maxImageHeight: 3000,
})

const emit = defineEmits(['update:modelValue', 'change'])
const preview = ref<string | undefined>('')
const errorInternal = ref('')

const errorMessage = computed(() => {
  if (errorInternal.value) {
    /* v8 ignore next - error only set by onDrop error paths that are difficult to cover -- @preserve */
    return errorInternal.value
  } else if (typeof props.error === 'string') {
    return props.error
  } else {
    return ''
  }
})

watch(
  () => props.thumbnail,
  (value) => {
    preview.value = value
  },
  { immediate: true },
)

watch(
  () => props.modelValue,
  (value) => {
    if (!value) {
      preview.value = ''
    }
  },
)

watch(errorInternal, (value) => {
  /* v8 ignore next 5 - difficult to cover because it auto-clears after 3s and would need a long wait -- @preserve  */
  if (value) {
    setTimeout(() => {
      errorInternal.value = ''
    }, 3000)
  }
})

function reset() {
  preview.value = ''
  emit('update:modelValue', null)
  emit('change')
}

function onDrop(acceptFiles: File[], rejectReasons: any[]) {
  /* v8 ignore next - dropzone library prevents drops when disabled, making this defensive -- @preserve */
  if (props.disabled) return
  /* v8 ignore next 4 - difficult to cover because it is the library reject path and hard to trigger with file input -- @preserve */
  if (rejectReasons.length) {
    errorInternal.value = rejectReasons[0].errors[0].message
    preview.value = ''
    return
  }

  /* v8 ignore next 4 - difficult to cover because maxFileSize is not wired from FormInput and would need prop plus large file -- @preserve */
  if (props.maxFileSize && acceptFiles[0].size > props.maxFileSize) {
    errorInternal.value = `File is too big, max size is ${props.maxFileSize / 1024 / 1024 + 'MB'}`
    preview.value = ''
    return
  }

  const reader = new FileReader()

  reader.readAsDataURL(acceptFiles[0])
  // When reading is complete
  reader.onload = () => {
    // read image dimensions
    const image = new Image()
    image.src = reader.result as string
    image.onload = () => {
      const { width, height } = image
      if (width > props.maxImageWidth && height > props.maxImageHeight) {
        errorInternal.value = `Image dimensions are too big, max width is ${props.maxImageWidth}px and max height is ${props.maxImageHeight}px`
        preview.value = ''
        return
      } else if (width > props.maxImageWidth) {
        errorInternal.value = `Image width is too big, max width is ${props.maxImageWidth}px`
        preview.value = ''
        return
      } else if (height > props.maxImageHeight) {
        errorInternal.value = `Image height is too big, max height is ${props.maxImageHeight}px`
        preview.value = ''
        return
      } else {
        const fileWithDimensions = acceptFiles[0] as FileWithDimensions
        fileWithDimensions.width = image.width
        fileWithDimensions.height = image.height

        emit('update:modelValue', fileWithDimensions)
        emit('change')
        preview.value = reader.result as string
      }
    }
  }
}

const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
  onDrop,
  multiple: false,
  accept: 'image/*',
  /* v8 ignore next 3 - getter called by library internals, difficult to cover in isolation -- @preserve */
  get disabled() {
    return props.disabled ?? false
  },
})

const dropzoneRootProps = computed(() => {
  const rootProps = getRootProps()
  return {
    ...rootProps,
    tabIndex: props.disabled ? -1 : 0,
    role: 'button',
    'aria-label': 'Choose image',
    'aria-disabled': props.disabled || undefined,
  }
})

/* v8 ignore next 7 - keyboard opens native file picker which cannot be tested in headless browser -- @preserve */
function onDropzoneKeydown(e: KeyboardEvent) {
  if (props.disabled) return
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault()
    open?.()
  }
}
</script>

<template>
  <div class="preview" v-if="preview">
    <img :src="preview" alt="" />
    <BaseButton
      type="button"
      class="remove"
      :iconAfter="IconTrash"
      size="xs"
      hierarchy="tertiary"
      intent="destructive"
      aria-label="Remove"
      :disabled="props.disabled"
      @click="reset()"
    />
  </div>
  <div
    v-else
    class="dropzone"
    :class="{ disabled: props.disabled }"
    v-bind="dropzoneRootProps"
    @keydown="onDropzoneKeydown"
  >
    <!-- @vue-ignore getInputProps types don't expect `name` and `id` props, but they do exist in the code: https://github.com/Yaxian/vue3-dropzone/blob/9375da078b5de1302b6efd16ee61de20c84cd532/src/__tests__/useDropzone.test.ts#L116 -->
    <input v-bind="getInputProps({ name: props.name, id: props.id })" />
    <BaseIcon class="upload-icon" :icon="IconUpload" />
    <div v-if="errorMessage" class="error">{{ errorMessage }}</div>
    <p v-else>
      <strong>Choose your image</strong><br />
      <span v-if="isDragActive">drop image here</span>
      <span v-else>or drag one here</span>
    </p>
  </div>
</template>

<style scoped>
.dropzone {
  border: 1px dashed var(--c-input-border);
  border-radius: var(--radius-small);
  padding: 1.25em;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  gap: 0.4375em;
  cursor: pointer;
  width: 100%;
  min-width: 10.3125em;

  &:focus {
    outline: none;
  }

  &:focus-visible,
  &:focus-within {
    border-color: var(--c-input-border-focus);
    box-shadow: var(--c-input-focus-visible);
  }

  &.disabled {
    cursor: default;
    pointer-events: none;
    border-color: var(--c-input-disabled-text);
    background: var(--c-input-disabled-background);
    color: var(--c-input-disabled-text);
  }

  .error {
    color: var(--c-input-border-error);
    font-weight: 600;
  }
}

.preview {
  width: auto;
  position: relative;
  border: 1px dashed var(--c-input-border);
  border-radius: var(--radius-small);

  img {
    max-width: 100%;
    display: flex;
    max-height: 21.875em;
  }

  .remove {
    position: absolute;
    bottom: 0.3125em;
    right: 0.3125em;
    z-index: 1;
  }
}
</style>
